import React from "react";
import { toast } from "sonner";
import { Unlink } from "lucide-react";
import { useGetAuthIdentitiesQuery, useUnlinkAuthIdentityMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { getProvider, providerInitials } from "@/util/integrationProviders";
import { AuthIdentity } from "@/model/interface/OAuthLogin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const EMAIL_PROVIDER = "EMAIL";

function providerLabel(identity: AuthIdentity): string {
  if (identity.provider === EMAIL_PROVIDER) {
    return "Email and password";
  }
  return getProvider(identity.provider.toLowerCase())?.name ?? identity.provider;
}

export default function LinkedSignInMethods() {
  const { data, isLoading } = useGetAuthIdentitiesQuery();
  const [unlink, unlinkResult] = useUnlinkAuthIdentityMutation();

  const identities = data?.identities ?? [];
  const onlyMethodNoPassword = !data?.hasPassword && identities.length <= 1;

  const onUnlink = async (identity: AuthIdentity) => {
    try {
      await unlink(identity.id).unwrap();
      toast.success("Sign-in method removed");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not remove this sign-in method."));
    }
  };

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle>Sign-in methods</CardTitle>
        <CardDescription>Accounts you can use to sign in to Retreevo.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          identities.map((identity) => {
            const isEmail = identity.provider === EMAIL_PROVIDER;
            const provider = isEmail ? undefined : getProvider(identity.provider.toLowerCase());
            return (
              <div
                key={identity.id}
                className="flex items-center justify-between gap-3 rounded-lg border p-3"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-xs font-semibold text-white"
                    style={{ backgroundColor: provider?.accent ?? "#64748b" }}
                  >
                    {isEmail ? "@" : providerInitials(provider?.name ?? identity.provider)}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{providerLabel(identity)}</p>
                    <p className="truncate text-xs text-muted-foreground">{identity.providerSubject}</p>
                  </div>
                </div>
                {isEmail ? (
                  <Badge variant="secondary">Primary</Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={onlyMethodNoPassword || unlinkResult.isLoading}
                    onClick={() => onUnlink(identity)}
                  >
                    <Unlink className="mr-1 h-4 w-4" />
                    Unlink
                  </Button>
                )}
              </div>
            );
          })
        )}
        {onlyMethodNoPassword && identities.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Set a password before removing your only sign-in method.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
